/**
 * Accessibility Tools — SPA adapter.
 *
 * Delegates all portable logic to @chromis/shared-logic.
 * Preserves the class interface for backward compatibility with existing components.
 */

// Re-export types directly from shared-logic
export type {
  TargetCompliance,
  ColorBlindnessType,
  ContrastOptions,
  ContrastResult,
  PaletteAuditPairCompliance,
  PaletteAutoFixProposal,
  PaletteAuditPairResult,
  PaletteConfusablePair,
  PaletteAuditReport,
  PaletteAuditOptions,
} from '@chromis/shared-logic';

import {
  calculateContrast,
  checkAACompliance,
  batchContrastCheck,
  simulateColorBlindness,
  detectConfusableColors,
  auditPalette,
  type ContrastOptions,
  type ContrastResult,
  type PaletteAuditOptions,
  type PaletteAuditReport,
  type ColorBlindnessType,
} from '@chromis/shared-logic';

/**
 * Thin class wrapper preserving the existing `accessibilityTools.method()` call pattern.
 * All methods delegate to standalone functions from @chromis/shared-logic.
 */
export class AccessibilityTools {
  calculateContrast(
    foreground: string,
    background: string,
    options?: ContrastOptions,
  ): ContrastResult | null {
    return calculateContrast(foreground, background, options);
  }

  checkAACompliance(
    foreground: string,
    background: string,
    options?: ContrastOptions,
  ): { compliant: boolean; level: 'AAA' | 'AA' | 'Fail'; ratio: number } | null {
    return checkAACompliance(foreground, background, options);
  }

  batchContrastCheck(
    foregrounds: string[],
    backgrounds: string[],
    options?: ContrastOptions,
  ): Array<{ fg: string; bg: string; result: ContrastResult | null }> {
    return batchContrastCheck(foregrounds, backgrounds, options);
  }

  simulateColorBlindness(color: string, type: ColorBlindnessType): string | null {
    return simulateColorBlindness(color, type);
  }

  detectConfusableColors(
    colors: string[],
    threshold?: number,
  ): Array<[string, string, number]> {
    return detectConfusableColors(colors, threshold);
  }

  auditPalette(
    colors: string[],
    options?: PaletteAuditOptions,
  ): PaletteAuditReport {
    return auditPalette(colors, options);
  }
}

export const accessibilityTools = new AccessibilityTools();
